import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import {
  STANDARD_FIELDS,
  FIELD_GROUP_TITLE_KEYS,
} from 'dashboard/components-next/Forms/standardFields';
import { SOURCE_ATTRIBUTE_KEYS } from 'dashboard/components-next/Forms/sourceAttributeKeys';

const ATTRIBUTE_GETTERS = {
  contact: 'attributes/getContactAttributes',
  company: 'attributes/getCompanyAttributes',
  deal: 'attributes/getDealAttributes',
  product: 'attributes/getProductAttributes',
};

/**
 * Unified, ordered list of the standard and custom fields of a model
 * ('contact' | 'company' | 'deal'), following `settings.field_order`.
 * Ids look like `standard:<key>` or `custom:<attributeKey>`. Fields missing
 * from the saved order go last; saved ids that no longer exist are ignored.
 */
export function useFieldOrder(model) {
  const { currentAccount, updateAccount } = useAccount();
  const attributes = useMapGetter(ATTRIBUTE_GETTERS[model]);

  const defaultFields = computed(() => {
    const standardFields = STANDARD_FIELDS[model].map(field => ({
      ...field,
      id: `standard:${field.key}`,
      type: 'standard',
    }));
    const customFields = (attributes.value || [])
      .filter(
        ({ attributeKey }) => !SOURCE_ATTRIBUTE_KEYS.includes(attributeKey)
      )
      .map(attribute => ({
        id: `custom:${attribute.attributeKey}`,
        key: attribute.attributeKey,
        type: 'custom',
        attribute,
      }));

    // Custom fields default next to the standard ones, before social
    // profiles (when the model has that field), so a fresh account doesn't
    // show them trailing behind "Redes sociais".
    const socialIndex = standardFields.findIndex(
      field => field.key === 'social_profiles'
    );
    if (socialIndex === -1) {
      return [...standardFields, ...customFields];
    }
    return [
      ...standardFields.slice(0, socialIndex),
      ...customFields,
      ...standardFields.slice(socialIndex),
    ];
  });

  const savedOrder = computed(
    () => currentAccount.value?.settings?.field_order?.[model] || []
  );

  const groupTitleKeys = FIELD_GROUP_TITLE_KEYS[model] || {};

  const orderedFields = computed(() => {
    const fieldsById = new Map(defaultFields.value.map(f => [f.id, f]));
    const saved = [...new Set(savedOrder.value)].filter(id =>
      fieldsById.has(id)
    );
    const savedIds = new Set(saved);
    const fields = [
      ...saved.map(id => fieldsById.get(id)),
      ...defaultFields.value.filter(({ id }) => !savedIds.has(id)),
    ];

    // A header is shown above the first field of each contiguous run of a
    // group, so reordering fields across groups keeps the headers in sync.
    return fields.map((field, index) => ({
      ...field,
      showGroupHeader:
        !!field.group && field.group !== fields[index - 1]?.group,
      groupTitleKey: groupTitleKeys[field.group],
    }));
  });

  // The API replaces `field_order` as a whole, so the other models' saved
  // lists are sent back untouched.
  const saveOrder = ids =>
    updateAccount(
      {
        field_order: {
          ...currentAccount.value?.settings?.field_order,
          [model]: ids,
        },
      },
      { silent: true }
    );

  return { orderedFields, saveOrder };
}
